import { TranslationInfo } from "@shared/types/translation-info";
import { HeaderDisplayType } from "@shared/components/table-implementation/bcl-table-data/header-display-type";
import { DataDisplayType } from "../../bcl-table-data/data-display-type";
import { SortDirection } from "../sorting-directions";
import { TableColumnConfig } from "./column-config.type";
import { IconName } from "@fortawesome/fontawesome-common-types";

/**
 * Resolves the column field key type.
 * @typeParam T - Type of which the field is a key. if provided field is keyOf T if not field defaults to string
 */
export type TableField<T = never> =
  [T] extends [never] ? string : Extract<keyof T, string>;
export interface TableColumnIdentifier<T = never> {
  readonly field: TableField<T>;
}
/**
 * Representation of a TableColumn.
 *
 * @typeParam  T - Type of which the field is a key. if provided field is keyOf T if not field defaults to string.
 * @typeParam  TApi - Type of which dataFields are keys from. if provided dataFields is an Array of keyOf T. TApi defaults to T.
 */
export interface TableColumn<T = never, TApi = T> extends TableColumnIdentifier<T> {
  dataDisplayType: DataDisplayType;
  /** Fields that are fetched for the column. */
  dataFields?: Array<TableField<TApi>>;
  /**
   * Whether the field is required and must always be fetched,
   * not only when the column is visible.
   */
  isRequiredField?: boolean;

  headerDisplayType: HeaderDisplayType;
  translationInfo?: TranslationInfo | undefined;
  icon?: IconName;
  options: HeaderOptions;
}
export interface TableColumnDto<T = never> extends TableColumnIdentifier<T> {
  options: HeaderOptions;
}
export interface HeaderOptions {
  widthPx: number;
  sortDirection: SortDirection;
  isVisible: boolean;
  isFixed?: boolean;
}
/** Converts a client {@link TableColumn} to API-Type {@link TableColumnDto}. */
export function parseToTableColumnDto<T = never, TApi = T> (
  header: TableColumn<T, TApi>
): TableColumnDto<T> {
  return { field: header.field, options: header.options };
}
/** Returns a column from the table config by its field key. */
export function getColumnOfTableConfig<T = never> (
  tableConfig: TableColumnConfig,
  field: TableField<T>
): TableColumn | undefined {
  return tableConfig.columns.find((header: TableColumn) => header.field === field);
}
/**
 * Sorts columns to match the given field order.
 * Generic variant that preserves typing when you pass typed fields/columns.
 */
export function getTableColumnsSortedByFields<T = never, TApi = T> (
  fields: Array<TableField<T>>,
  headers: Array<TableColumn<T, TApi>>
): Array<TableColumn<T, TApi>> {
  const sortedHeaders: Array<TableColumn<T, TApi>> = [];

  fields.forEach((field: TableField<T>) => {
    const matchingColumn: TableColumn<T, TApi> | undefined = headers.find((header: TableColumn<T, TApi>) => header.field === field);

    if (matchingColumn) sortedHeaders.push(matchingColumn);
  });

  return sortedHeaders;
}

